/**
 * PlantasIotHistoryPanel.jsx  v1
 * Vista "Histórico" (base plantas_iot) — mismo formato que HistoryPanel de Alvear
 * Variables: Nivel % · Volumen m³ · Masa tn — todos los tanques juntos
 * Abajo: alarmas del período
 */

import { useState, useEffect } from 'react';
import {
    Box, Chip, CircularProgress, LinearProgress, Paper,
    Stack, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import { NotificationsActive as AlarmIcon } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import RangoFechas, { mesActual } from '../alvear/Rangofechas';

// Mismos colores por tanque que PlantasIotStripHistoryModal
const COLORES_TANQUE = ['#38BDF8', '#FB923C', '#A78BFA', '#22C55E', '#F472B6', '#FACC15', '#2DD4BF', '#F87171'];

const VARS = {
    nivel: { label: 'Nivel', sufijo: 'pct', unit: '%', min: 0, max: 100 },
    volumen: { label: 'Volumen', sufijo: 'm3', unit: 'm³' },
    masa: { label: 'Masa', sufijo: 'tn', unit: 'tn' },
};

const LIMITE_ALARMAS = 1000;

// Color de la alarma según su tipo: HH / LL críticas, H / L avisos
function severidad(tipo) {
    const t = (tipo || '').toUpperCase();
    if (t.endsWith('_HH') || t.endsWith('_LL')) return 'error';
    if (t.endsWith('_H') || t.endsWith('_L')) return 'warning';
    return 'info';
}

export default function PlantasIotHistoryPanel({ plantaId, tanques, apiUrl }) {
    const theme = useTheme();
    const [variable, setVariable] = useState('nivel');
    const [rango, setRango] = useState(() => mesActual());
    const [filas, setFilas] = useState([]);
    const [alarmas, setAlarmas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAl, setLoadingAl] = useState(false);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };
    const base = `${apiUrl}/api/plantas-iot/${plantaId}`;
    const vd = VARS[variable];

    // ── Histórico de todos los tanques ────────────────────────────────────────
    useEffect(() => {
        if (!plantaId) return;
        let activo = true;
        setLoading(true);
        fetch(`${base}/history?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&puntos=300`, { headers })
            .then((r) => r.json())
            .then((d) => {
                if (!activo) return;
                // Pivotear: una fila por momento, columnas por tanque
                const porTs = new Map();
                (d.puntos || []).forEach((p) => {
                    if (!porTs.has(p.ts)) porTs.set(p.ts, { ts: p.ts, fecha: dayjs(p.ts).format('DD/MM HH:mm') });
                    const f = porTs.get(p.ts);
                    f[`tk${p.tanque_id}_pct`] = p.volumen_pct;
                    f[`tk${p.tanque_id}_m3`] = p.volumen_m3;
                    f[`tk${p.tanque_id}_tn`] = p.masa_kg !== null ? Number((p.masa_kg / 1000).toFixed(2)) : null;
                });
                setFilas([...porTs.values()].sort((a, b) => (a.ts < b.ts ? -1 : 1)));
            })
            .catch(() => activo && setFilas([]))
            .finally(() => activo && setLoading(false));
        return () => { activo = false; };
    }, [plantaId, rango, apiUrl]);

    // ── Alarmas del período ───────────────────────────────────────────────────
    useEffect(() => {
        if (!plantaId) return;
        let activo = true;
        setLoadingAl(true);
        fetch(`${base}/eventos?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&limit=${LIMITE_ALARMAS}`, { headers })
            .then((r) => r.json())
            .then((d) => activo && setAlarmas(d.eventos || []))
            .catch(() => activo && setAlarmas([]))
            .finally(() => activo && setLoadingAl(false));
        return () => { activo = false; };
    }, [plantaId, rango, apiUrl]);

    const lista = (tanques || []).map((t, i) => ({ ...t, color: COLORES_TANQUE[i % COLORES_TANQUE.length] }));
    const tickCada = Math.max(1, Math.ceil(filas.length / 8));

    return (
        <>
            {/* Selector de variable */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                alignItems="flex-start" mb={2} gap={1.5}>
                <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{vd.label} · histórico</Typography>
                    <Typography variant="caption" color="text.disabled">
                        {filas.length} puntos · plantas_iot.{plantaId}
                    </Typography>
                </Box>
                <ToggleButtonGroup value={variable} exclusive onChange={(_, v) => v && setVariable(v)} size="small">
                    {Object.entries(VARS).map(([k, v]) => (
                        <ToggleButton key={k} value={k} sx={{ px: 1.5, fontSize: 11, py: 0.5 }}>
                            {v.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            {/* Selector de rango */}
            <Box sx={{ mb: 2 }}>
                <RangoFechas value={rango} onChange={setRango} />
            </Box>

            {/* Gráfico */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, position: 'relative' }}>
                {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
                        <LinearProgress sx={{ borderRadius: '4px 4px 0 0' }} />
                    </Box>
                )}
                <Stack direction="row" spacing={1.5} mb={1} flexWrap="wrap">
                    {lista.map((t) => (
                        <Stack key={t.tanque_id} direction="row" alignItems="center" spacing={0.5}>
                            <Box sx={{ width: 12, height: 2, bgcolor: t.color, borderRadius: 1 }} />
                            <Typography variant="caption" color="text.disabled" fontSize={10}>{t.tanque}</Typography>
                        </Stack>
                    ))}
                </Stack>
                {filas.length > 0 ? (
                    <LineChart
                        dataset={filas}
                        series={lista.map((t) => ({
                            dataKey: `tk${t.tanque_id}_${vd.sufijo}`,
                            label: t.tanque,
                            color: t.color,
                            showMark: false,
                            curve: 'monotoneX',
                            connectNulls: true,
                        }))}
                        xAxis={[{
                            dataKey: 'fecha',
                            scaleType: 'point',
                            tickLabelStyle: { fontSize: 8 },
                            tickInterval: (_, i) => i % tickCada === 0,
                        }]}
                        yAxis={[{ min: vd.min, max: vd.max, tickLabelStyle: { fontSize: 9 }, label: vd.unit }]}
                        height={260}
                        margin={{ left: 50, right: 16, top: 8, bottom: 24 }}
                        slotProps={{ legend: { hidden: true } }}
                    />
                ) : (
                    <Stack alignItems="center" py={4}>
                        {loading
                            ? <CircularProgress size={24} />
                            : <Typography variant="body2" color="text.disabled">Sin datos en el período seleccionado</Typography>
                        }
                    </Stack>
                )}
            </Paper>

            {/* Alarmas del período */}
            <Paper variant="outlined">
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" fontWeight={600} fontSize={11}>
                            Alarmas del período
                        </Typography>
                        {loadingAl
                            ? <CircularProgress size={14} />
                            : <Typography variant="caption" color="text.disabled" fontSize={10}>
                                {alarmas.length} alarma{alarmas.length !== 1 ? 's' : ''}
                                {alarmas.length >= LIMITE_ALARMAS ? ' (se muestran las más recientes)' : ''}
                            </Typography>
                        }
                    </Stack>
                </Box>

                {loadingAl ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size={24} />
                    </Stack>
                ) : alarmas.length === 0 ? (
                    <Stack alignItems="center" py={3}>
                        <Typography variant="body2" color="text.disabled">Sin alarmas en el período</Typography>
                    </Stack>
                ) : (
                    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                        {alarmas.map((a, i) => {
                            const sev = severidad(a.tipo);
                            const tq = lista.find((t) => t.tanque_id === a.tanque_id);
                            return (
                                <Box key={a.id} sx={{
                                    display: 'flex', alignItems: 'center',
                                    px: 1.5, py: 1,
                                    borderBottom: i < alarmas.length - 1 ? 1 : 0,
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <AlarmIcon sx={{ color: `${sev}.main`, fontSize: 18, flexShrink: 0 }} />
                                        <Box>
                                            <Stack direction="row" alignItems="center" spacing={0.8} flexWrap="wrap">
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tq ? tq.color : 'text.disabled', flexShrink: 0 }} />
                                                <Typography variant="caption" fontWeight={600} fontSize={11}>
                                                    {a.tanque || 'Planta'}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={a.tipo}
                                                    sx={{
                                                        height: 18, fontSize: 9, fontWeight: 700,
                                                        bgcolor: alpha(theme.palette[sev].main, 0.1),
                                                        color: `${sev}.main`,
                                                        '& .MuiChip-label': { px: 0.8 },
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.disabled" fontSize={10}>
                                                    {dayjs(a.ts).format('DD/MM/YYYY HH:mm:ss')}
                                                </Typography>
                                            </Stack>
                                            {a.detalle && (
                                                <Typography variant="caption" color="text.disabled" fontSize={10}>
                                                    {a.detalle}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>
        </>
    );
}